import React from 'react';
import { Dataset, HierarchyState } from '../../types';

interface ControlsProps {
  datasets: Dataset[];
  selectedDataset: string;
  onSelectDataset: (id: string) => void;
  hierarchyState: HierarchyState;
  onLevelChange: (levelId: string) => void;
  onDrillDown: (groupId: string) => void;
  onDrillUp: () => void;
  onToggleLabels?: () => void;
  onResetView?: () => void;
  showLabels?: boolean;
}

const Controls: React.FC<ControlsProps> = ({
  datasets,
  selectedDataset,
  onSelectDataset,
  hierarchyState,
  onLevelChange,
  onDrillDown,
  onDrillUp,
  onToggleLabels,
  onResetView,
  showLabels = true,
}) => {
  // Get current level information
  const currentLevel = hierarchyState.levels[hierarchyState.currentLevelId] || null;
  const hasParent = currentLevel?.parentLevel !== undefined;
  const hasChildren = currentLevel?.childLevels !== undefined && currentLevel.childLevels.length > 0;

  // Handle dataset selection with error checking
  const handleDatasetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    try {
      const datasetId = e.target.value;
      if (datasets.some((d) => d.id === datasetId)) {
        onSelectDataset(datasetId);
      } else {
        console.error(`Invalid dataset ID: ${datasetId}`);
      }
    } catch (error) {
      console.error('Error changing dataset:', error);
    }
  };

  return (
    <div className="controls-section" role="region" aria-label="Visualization Controls">
      <div className="controls-row">
        <div className="form-group">
          <label htmlFor="dataset-select" className="form-label">
            Dataset
          </label>
          <select
            id="dataset-select"
            className="form-control form-select"
            value={selectedDataset}
            onChange={handleDatasetChange}
            aria-describedby="dataset-description"
          >
            {datasets.map((dataset) => (
              <option key={dataset.id} value={dataset.id}>
                {dataset.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" id="navigation-label">
            Navigation
          </label>
          <div className="btn-group" role="group" aria-labelledby="navigation-label">
            <button
              className="btn btn-secondary"
              disabled={!hasParent}
              onClick={onDrillUp}
              title="Go to parent level"
              aria-label="Navigate up"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
              Up
            </button>

            {currentLevel && hasChildren && (
              <button
                className="btn btn-secondary"
                onClick={() => onLevelChange(currentLevel.childLevels![0])}
                title="Go to next level"
                aria-label="Navigate down"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
                Down
              </button>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" id="settings-label">
            View Settings
          </label>
          <div className="btn-group" role="group" aria-labelledby="settings-label">
            <button
              className={`btn btn-secondary ${showLabels ? 'active' : ''}`}
              title="Toggle axes labels"
              onClick={onToggleLabels}
              aria-pressed={showLabels}
              aria-label="Toggle labels"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
              Labels
            </button>

            <button className="btn btn-secondary" title="Reset view" onClick={onResetView} aria-label="Reset view">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
              </svg>
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="controls-row">
        <div className="dataset-info" id="dataset-description">
          {datasets.find((d) => d.id === selectedDataset)?.description || 'No description available'}
        </div>

        <div className="help-text" role="note">
          <span className="badge badge-primary">Tip</span> Select correlated dimension groups in the summary view to
          explore in the parallel coordinate plot.
        </div>
      </div>
    </div>
  );
};

export default Controls;
